'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Stat {
  id: number;
  value: string;
  label: string;
  icon: string;
  description: string;
}

interface ExportStatsSectionProps {
  className?: string;
}

const CountingNumber = ({ end, suffix = '' }: { end: number; suffix?: string }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          const duration = 2000;
          const steps = 60;
          const increment = end / steps;
          const stepDuration = duration / steps;
          let current = 0;

          const timer = setInterval(() => {
            current += increment;
            if (current >= end) {
              setCount(end);
              setHasAnimated(true);
              clearInterval(timer);
            } else {
              setCount(Math.floor(current));
            }
          }, stepDuration);

          return () => clearInterval(timer);
        }
      },
      { threshold: 0.5 }
    );

    const element = document.getElementById(`stat-counter-${end}`);
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, [end, hasAnimated]);

  return <span data-component-id="src/app/export-capabilities/components/ExportStatsSection.tsx:55:9" data-component-path="src/app/export-capabilities/components/ExportStatsSection.tsx" data-component-line="55" data-component-end-line="55" data-component-file="ExportStatsSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D" id={`stat-counter-${end}`}>{count}{suffix}</span>;
};

const ExportStatsSection = ({ className = '' }: ExportStatsSectionProps) => {
  const stats: Stat[] = [
    {
      id: 1,
      value: "15+",
      label: "Export Markets",
      icon: "GlobeAsiaAustraliaIcon",
      description: "Active international partnerships"
    },
    {
      id: 2,
      value: "500+",
      label: "Containers Shipped",
      icon: "TruckIcon",
      description: "Annual export volume"
    },
    {
      id: 3,
      value: "99.8%",
      label: "On-Time Delivery",
      icon: "ClockIcon",
      description: "Reliable logistics performance"
    },
    {
      id: 4,
      value: "100%",
      label: "Compliance Rate",
      icon: "ShieldCheckIcon",
      description: "Quality & regulatory standards"
    }
  ];

  const parseValue = (value: string): { number: number; suffix: string } => {
    const match = value.match(/([0-9.]+)(.*)/);
    if (match) {
      return { number: parseFloat(match[1]), suffix: match[2] };
    }
    return { number: 0, suffix: '' };
  };

  return (
    <section data-component-id="src/app/export-capabilities/components/ExportStatsSection.tsx:99:4" data-component-path="src/app/export-capabilities/components/ExportStatsSection.tsx" data-component-line="99" data-component-end-line="129" data-component-file="ExportStatsSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%7D" className={`py-16 lg:py-20 bg-card ${className}`}>
      <div data-component-id="src/app/export-capabilities/components/ExportStatsSection.tsx:100:6" data-component-path="src/app/export-capabilities/components/ExportStatsSection.tsx" data-component-line="100" data-component-end-line="128" data-component-file="ExportStatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%22%7D" className="container mx-auto px-4">
        <div data-component-id="src/app/export-capabilities/components/ExportStatsSection.tsx:101:8" data-component-path="src/app/export-capabilities/components/ExportStatsSection.tsx" data-component-line="101" data-component-end-line="127" data-component-file="ExportStatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20sm%3Agrid-cols-2%20lg%3Agrid-cols-4%20gap-6%20lg%3Agap-8%22%7D" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {stats.map((stat, index) => {
            const { number, suffix } = parseValue(stat.value);
            return (
              <div data-component-id="src/app/export-capabilities/components/ExportStatsSection.tsx:105:14" data-component-path="src/app/export-capabilities/components/ExportStatsSection.tsx" data-component-line="105" data-component-end-line="124" data-component-file="ExportStatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-background%20rounded-xl%20p-6%20lg%3Ap-8%20shadow-card%20hover%3Ashadow-elevated%20transition-all%20duration-500%20text-center%20space-y-3%20hover-lift%20animate-fade-in-up%22%7D"
                key={stat.id}
                className="bg-background rounded-xl p-6 lg:p-8 shadow-card hover:shadow-elevated transition-all duration-500 text-center space-y-3 hover-lift animate-fade-in-up"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div data-component-id="src/app/export-capabilities/components/ExportStatsSection.tsx:110:16" data-component-path="src/app/export-capabilities/components/ExportStatsSection.tsx" data-component-line="110" data-component-end-line="112" data-component-file="ExportStatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22inline-flex%20items-center%20justify-center%20w-14%20h-14%20bg-primary%2F10%20rounded-full%20transition-all%20duration-300%20hover%3Ascale-110%20hover%3Arotate-12%22%7D" className="inline-flex items-center justify-center w-14 h-14 bg-primary/10 rounded-full transition-all duration-300 hover:scale-110 hover:rotate-12">
                  <Icon data-component-id="src/app/export-capabilities/components/ExportStatsSection.tsx:111:18" data-component-path="src/app/export-capabilities/components/ExportStatsSection.tsx" data-component-line="111" data-component-end-line="111" data-component-file="ExportStatsSection.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22text-primary%22%7D" name={stat.icon as any} size={28} className="text-primary" />
                </div>
                <div data-component-id="src/app/export-capabilities/components/ExportStatsSection.tsx:113:16" data-component-path="src/app/export-capabilities/components/ExportStatsSection.tsx" data-component-line="113" data-component-end-line="123" data-component-file="ExportStatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-1%22%7D" className="space-y-1">
                  <div data-component-id="src/app/export-capabilities/components/ExportStatsSection.tsx:114:18" data-component-path="src/app/export-capabilities/components/ExportStatsSection.tsx" data-component-line="114" data-component-end-line="116" data-component-file="ExportStatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22font-headline%20font-bold%20text-4xl%20lg%3Atext-5xl%20text-primary%22%7D" className="font-headline font-bold text-4xl lg:text-5xl text-primary">
                    <CountingNumber data-component-id="src/app/export-capabilities/components/ExportStatsSection.tsx:115:20" data-component-path="src/app/export-capabilities/components/ExportStatsSection.tsx" data-component-line="115" data-component-end-line="115" data-component-file="ExportStatsSection.tsx" data-component-name="CountingNumber" data-component-content="%7B%22elementName%22%3A%22CountingNumber%22%7D" end={number} suffix={suffix} />
                  </div>
                  <div data-component-id="src/app/export-capabilities/components/ExportStatsSection.tsx:117:18" data-component-path="src/app/export-capabilities/components/ExportStatsSection.tsx" data-component-line="117" data-component-end-line="119" data-component-file="ExportStatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22font-headline%20font-semibold%20text-base%20text-foreground%22%7D" className="font-headline font-semibold text-base text-foreground">
                    {stat.label}
                  </div>
                  <p data-component-id="src/app/export-capabilities/components/ExportStatsSection.tsx:120:18" data-component-path="src/app/export-capabilities/components/ExportStatsSection.tsx" data-component-line="120" data-component-end-line="122" data-component-file="ExportStatsSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%22%7D" className="text-sm text-muted-foreground">
                    {stat.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ExportStatsSection;